import type { MapLayerDefinition, PluginClientConfig } from '@vtak/shared';
import { usePluginStore } from '@/stores/plugin-store';

/** Collected layer info from all active plugins */
export interface ActiveMapLayer {
  /** Full layer ID: pluginId:layerId */
  fullId: string;
  /** Plugin that owns this layer */
  pluginId: string;
  /** Layer definition from the plugin */
  definition: MapLayerDefinition;
  /** Whether currently visible */
  visible: boolean;
}

/**
 * Build the list of active map layers from loaded plugins.
 * Each plugin's layers are namespaced with the plugin ID.
 */
export function buildActiveMapLayers(plugins: PluginClientConfig[]): ActiveMapLayer[] {
  const layers: ActiveMapLayer[] = [];

  for (const plugin of plugins) {
    for (const layerDef of plugin.mapLayers) {
      // Invariant: fullId must equal the WS event type (the plugin-data-store
      // key). A layer id already carrying the plugin prefix is used as-is —
      // prefixing again would yield `plugin:plugin:layer` and never match.
      const prefix = `${plugin.id}:`;
      layers.push({
        fullId: layerDef.id.startsWith(prefix) ? layerDef.id : `${prefix}${layerDef.id}`,
        pluginId: plugin.id,
        definition: layerDef,
        visible: layerDef.defaultVisible,
      });
    }
  }

  return layers;
}

/**
 * Group layers by category for the layer switcher UI.
 * Categories: operational, reference, threat, environment
 */
export function groupLayersByCategory(
  layers: ActiveMapLayer[],
): Map<string, ActiveMapLayer[]> {
  const groups = new Map<string, ActiveMapLayer[]>();

  for (const layer of layers) {
    const category = layer.definition.category;
    const existing = groups.get(category) ?? [];
    existing.push(layer);
    groups.set(category, existing);
  }

  return groups;
}

/** Find the active map-layer definition for a full layer id (`pluginId:layerId`). */
export function findLayerDefinition(fullId: string): MapLayerDefinition | undefined {
  const layers = buildActiveMapLayers(usePluginStore.getState().activePlugins);
  return layers.find((layer) => layer.fullId === fullId)?.definition;
}

/** Category display labels */
export const CATEGORY_LABELS = new Map([
  ['operational', 'Operational'],
  ['reference', 'Reference'],
  ['threat', 'Threat'],
  ['environment', 'Environment'],
]);
